import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';
import type { IntlShape } from 'react-intl';
import { defineMessages } from 'react-intl';
import type { Node } from 'prosemirror-model';

import Tooltip from '@atlaskit/tooltip/Tooltip';
import {
	ACTION,
	ACTION_SUBJECT,
	ACTION_SUBJECT_ID,
	EVENT_TYPE,
} from '@atlaskit/editor-common/analytics/types/enums';
import type { EditorAnalyticsAPI } from '@atlaskit/editor-common/analytics/api';
import { useThemeObserver } from '@atlaskit/tokens/use-theme-observer';

import { isExperimentEnabled } from '@confluence/feature-experiments';

import { copyLink } from './headingLinksUtils.ts';

const i18n = defineMessages({
	tooltipCopyMessage: {
		id: 'editor-heading-links.link-component.tooltip.copy',
		defaultMessage: 'Copy link to heading',
		description: 'The message shown in the tooltip when the user hovers over the link icon',
	},
	tooltipCopiedMessage: {
		id: 'editor-heading-links.link-component.tooltip.copied',
		defaultMessage: 'Copied!',
		description: 'The message shown in the tooltip once the user clicks the link icon',
	},
	iconLabel: {
		id: 'editor-heading-links.link-component.icon.label',
		defaultMessage: 'Link icon',
		description:
			'The text is shown as the accessible label for the link icon button that appears beside headings in the editor. When clicked, it copies the anchor link to that heading to the clipboard.',
	},
});

type LinkCopyComponentProps = {
	node: Node;
	// intl and createAnalyticsEvent must be passed in
	// because this component is rendered outside of the
	// main React tree and therefore cannot access the
	// necessary contexts
	intl: IntlShape;
	analytics: EditorAnalyticsAPI | undefined;
	isRightAligned: boolean;
	getHeadingLinkPath: () => string | null;
};
export const LinkCopyComponent = ({
	node,
	intl,
	analytics,
	isRightAligned,
	getHeadingLinkPath,
}: LinkCopyComponentProps): React.JSX.Element => {
	const [didCopy, setDidCopy] = useState(false);
	const updateTooltip = useRef<() => void>();
	const { colorMode } = useThemeObserver();

	useLayoutEffect(() => {
		updateTooltip.current?.();
	}, [didCopy]);

	const handleClick = useCallback(async () => {
		const didCopySucceed = await copyLink(node, getHeadingLinkPath);
		if (didCopySucceed) {
			setDidCopy(true);
		}

		// Should match the event fired for heading links on the view page
		// platform/packages/editor/renderer/src/react/nodes/heading.tsx
		analytics?.fireAnalyticsEvent?.({
			action: ACTION.CLICKED,
			actionSubject: ACTION_SUBJECT.BUTTON,
			actionSubjectId: ACTION_SUBJECT_ID.HEADING_ANCHOR_LINK,
			eventType: EVENT_TYPE.UI,
		});
	}, [node, analytics, getHeadingLinkPath]);

	const tooltipContentFn = useCallback(
		({ update }: { update?: () => void }) => {
			updateTooltip.current = update;
			return didCopy
				? intl.formatMessage(i18n.tooltipCopiedMessage)
				: intl.formatMessage(i18n.tooltipCopyMessage);
		},
		[didCopy, intl],
	);

	const handleMouseOut = useCallback(() => {
		setDidCopy(false);
	}, []);

	const headingLevel = node.attrs['level'] as number;
	const alignmentClass = isRightAligned ? 'link-widget-right' : 'link-widget-left';
	const sizeClass = headingLevel > 3 ? 'link-widget-small' : 'link-widget-medium';
	const isHeadingLinkTargetSizeExperimentEnabled = isExperimentEnabled(
		'platform_editor_heading_link_target_size',
	);
	const experimentSizeClass = isHeadingLinkTargetSizeExperimentEnabled
		? 'link-widget-medium'
		: sizeClass;
	let colorClass;
	if (didCopy) {
		colorClass = colorMode === 'dark' ? 'link-widget-clicked-dark' : 'link-widget-clicked-light';
	} else {
		colorClass = colorMode === 'dark' ? 'link-widget-hovered-dark' : 'link-widget-hovered-light';
	}

	return (
		<Tooltip
			tag="span"
			delay={0}
			position="top"
			content={tooltipContentFn}
			hasNewContentOnTriggerClick
		>
			{isHeadingLinkTargetSizeExperimentEnabled ? (
				// Keep the existing hover visibility and alignment wrapper without affecting heading layout.
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Existing heading decoration styles own visibility and alignment.
				<span className={`link-widget ${alignmentClass} ${experimentSizeClass}`}>
					<button
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- The heading style wrapper owns decoration layout and visibility.
						className="heading-link-copy-button"
						type="button"
						data-testid="heading-link-copy-button"
						aria-label={intl.formatMessage(
							didCopy ? i18n.tooltipCopiedMessage : i18n.tooltipCopyMessage,
						)}
						onClick={handleClick}
						onMouseLeave={handleMouseOut}
						onBlur={handleMouseOut}
					>
						<span
							aria-hidden="true"
							data-testid="heading-link-copy-button-icon"
							// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Reuse the existing theme-aware SVG pseudo-element.
							className={`heading-link-copy-button-icon ${experimentSizeClass} ${colorClass}`}
						/>
					</button>
				</span>
			) : (
				// eslint-disable-next-line @atlassian/a11y/click-events-have-key-events, @atlassian/a11y/interactive-element-not-keyboard-focusable, @atlassian/a11y/no-static-element-interactions
				<span
					data-testid="heading-link-copy-button"
					// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
					className={`link-widget ${alignmentClass} ${sizeClass} ${colorClass}`}
					onClick={handleClick}
					// eslint-disable-next-line @atlassian/a11y/mouse-events-have-key-events
					onMouseLeave={handleMouseOut}
				/>
			)}
		</Tooltip>
	);
};
